import { createContext, useContext, useState, useEffect, useMemo } from 'react';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦', dir: 'rtl' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹' },
];

const TRANSLATIONS = {
  en: {
    // Nav
    nav_dashboard: 'Dashboard',
    nav_new_order: 'New Order',
    nav_services: 'Services',
    nav_orders: 'Orders',
    nav_deposit: 'Deposit',
    nav_virtual_numbers: 'Virtual Numbers',
    nav_referrals: 'Referral Program',
    nav_settings: 'Settings',
    nav_support: 'Support',
    nav_guide: 'Guide',
    nav_logout: 'Logout',
    nav_login: 'Log In',
    nav_signup: 'Get Started',
    nav_features: 'Features',
    nav_platforms: 'Platforms',
    nav_how_it_works: 'How It Works',
    nav_pricing: 'Pricing',
    nav_faq: 'FAQ',
    nav_language: 'Language',

    // Settings
    settings_title: 'Account Settings',
    settings_subtitle: 'Manage your profile preferences, regional currency, security credentials, and API access.',
    tab_preferences: 'Preferences & Currency',
    tab_security: 'Security & Password',
    tab_api: 'API & Reseller Key',
    loc_title: 'Localization & Currency',
    loc_subtitle: 'Set your location, preferred language and currency for orders, balance, and payment methods.',
    field_country: 'Primary Country',
    field_country_desc: 'Mobile Money and localized payment options will default to this country.',
    field_currency: 'Account Display Currency',
    field_language: 'Preferred Language',
    field_language_desc: 'Select the display language for the platform interface and notifications.',
    btn_save_preferences: 'Save Preferences',
    btn_saving_preferences: 'Updating Preferences...',
    msg_preferences_saved: 'Preferences updated successfully!',

    // Dashboard
    dash_smm_services: 'Social Media Services',
    dash_smm_desc: 'Boost your followers, likes, views & engagement across all platforms',
    dash_vn_title: 'Virtual Numbers',
    dash_vn_desc: 'Receive instant SMS verification codes with private rental numbers',
    dash_popular_smm: 'Popular Social Media Services',
    dash_see_all_services: 'See all services',
    dash_popular_vn: 'Popular Virtual Numbers',
    dash_browse_countries: 'Browse all countries',
    dash_top_up: '+ Top up',
    dash_total_orders: 'Total Orders',
    dash_all_time_completed: 'All time completed',
    dash_total_spent: 'Total Spent',
    dash_lifetime_volume: 'Lifetime volume',
    dash_vn_used: 'Virtual Numbers Used',
    dash_sms_verifications: 'SMS verifications',
    dash_recent_orders: 'Recent Orders',
    dash_recent_subtitle: 'Track your latest service orders and delivery status',
    dash_refresh: 'Refresh',
    dash_view_all_orders: 'View All Orders →',
    col_order: 'Order',
    col_service: 'Service',
    col_quantity: 'Quantity',
    col_cost: 'Cost',
    col_status: 'Status',
    col_date: 'Date',
    no_orders_placed: 'No orders placed yet',
    no_orders_desc: 'Select a service from our catalog to boost your channels today.',
    place_first_order: 'Place First Order',

    // Landing Page
    hero_title_1: 'Accelerate Your Social Growth at',
    hero_title_2: 'Wholesale Rates',
    hero_subtitle: 'The all-in-one automated platform for creators, agencies, and businesses. Boost followers, likes, views across all major networks, and get instant virtual phone numbers for SMS verification.',
    hero_cta_free: 'Get Started Free',
    hero_view_catalog: 'View Services Catalog',
    hero_trusted: 'Trusted by 50,000+ creators • MoMo & Crypto accepted',
    stat_delivered_today: 'Delivered Today',
    stat_sms_received: 'SMS Received',
    stat_orders_completed: 'Orders Completed',
    stat_active_creators: 'Active Creators & Agencies',
    stat_uptime: 'Automated System Uptime',
    stat_speed: 'Average Start Speed',
    live_order_network: 'Live Order Network',
    recent_activity: 'Recent Activity',
    final_cta_title: 'Supercharge Your Social Presence Today',
    final_cta_subtitle: 'Join over 50,000 creators, digital brands, and marketing agencies growing effortlessly with NiceBooster.',
    create_free_account: 'Create Free Account',
    talk_to_support: 'Talk to Support',
    footer_nav: 'Navigation',
    footer_account: 'Account',
    footer_support: 'Support & Legal',

    // Common
    available_balance: 'Available Balance',
    add_funds: 'Add Funds',
    place_order: 'Place Order',
    category: 'Category',
    service: 'Service',
    quantity: 'Quantity',
    link: 'Link',
    total_cost: 'Total Cost',
    status: 'Status',
    actions: 'Actions',
    search: 'Search',
    cancel: 'Cancel',
    loading: 'Loading...',
    close: 'Close',
  },
  fr: {
    // Nav
    nav_dashboard: 'Tableau de bord',
    nav_new_order: 'Nouvelle commande',
    nav_services: 'Services',
    nav_orders: 'Commandes',
    nav_deposit: 'Recharger',
    nav_virtual_numbers: 'Numéros virtuels',
    nav_referrals: 'Parrainage',
    nav_settings: 'Paramètres',
    nav_support: 'Support',
    nav_guide: 'Guide',
    nav_logout: 'Déconnexion',
    nav_login: 'Connexion',
    nav_signup: 'Commencer',
    nav_features: 'Fonctionnalités',
    nav_platforms: 'Plateformes',
    nav_how_it_works: 'Comment ça marche',
    nav_pricing: 'Tarifs',
    nav_faq: 'FAQ',
    nav_language: 'Langue',

    // Settings
    settings_title: 'Paramètres du compte',
    settings_subtitle: 'Gérez vos préférences de profil, devise régionale, sécurité et accès API.',
    tab_preferences: 'Préférences & Devise',
    tab_security: 'Sécurité & Mot de passe',
    tab_api: 'Clé API & Revendeur',
    loc_title: 'Localisation, Devise & Langue',
    loc_subtitle: 'Définissez votre pays, langue préférée et devise pour vos commandes et paiements.',
    field_country: 'Pays principal',
    field_country_desc: 'Les paiements Mobile Money et options locales utiliseront ce pays par défaut.',
    field_currency: 'Devise d\'affichage du compte',
    field_language: 'Langue préférée',
    field_language_desc: 'Sélectionnez la langue d\'affichage de la plateforme et des notifications.',
    btn_save_preferences: 'Enregistrer les préférences',
    btn_saving_preferences: 'Mise à jour en cours...',
    msg_preferences_saved: 'Préférences mises à jour avec succès !',

    // Dashboard
    dash_smm_services: 'Services Réseaux Sociaux',
    dash_smm_desc: 'Boostez vos abonnés, likes, vues et engagements sur toutes les plateformes',
    dash_vn_title: 'Numéros Virtuels',
    dash_vn_desc: 'Recevez instantanément des codes SMS de vérification avec des numéros privés',
    dash_popular_smm: 'Services Réseaux Sociaux Populaires',
    dash_see_all_services: 'Voir tous les services',
    dash_popular_vn: 'Numéros Virtuels Populaires',
    dash_browse_countries: 'Voir tous les pays',
    dash_top_up: '+ Recharger',
    dash_total_orders: 'Total des commandes',
    dash_all_time_completed: 'Total finalisé',
    dash_total_spent: 'Total dépensé',
    dash_lifetime_volume: 'Volume cumulé',
    dash_vn_used: 'Numéros virtuels utilisés',
    dash_sms_verifications: 'Vérifications SMS',
    dash_recent_orders: 'Commandes récentes',
    dash_recent_subtitle: 'Suivez vos dernières commandes et l\'état de livraison',
    dash_refresh: 'Actualiser',
    dash_view_all_orders: 'Toutes les commandes →',
    col_order: 'Commande',
    col_service: 'Service',
    col_quantity: 'Quantité',
    col_cost: 'Coût',
    col_status: 'Statut',
    col_date: 'Date',
    no_orders_placed: 'Aucune commande pour le moment',
    no_orders_desc: 'Sélectionnez un service dans notre catalogue pour propulser vos réseaux.',
    place_first_order: 'Passer une première commande',

    // Landing Page
    hero_title_1: 'Accélérez votre croissance sur les réseaux au',
    hero_title_2: 'Tarif de Gros',
    hero_subtitle: 'La plateforme automatisée tout-en-un pour créateurs, agences et entreprises. Boostez vos abonnés, likes, vues et obtenez des numéros virtuels instantanés pour vos vérifications SMS.',
    hero_cta_free: 'Commencer gratuitement',
    hero_view_catalog: 'Voir le catalogue de services',
    hero_trusted: 'Approuvé par 50 000+ créateurs • MoMo & Crypto acceptés',
    stat_delivered_today: 'Livrées aujourd\'hui',
    stat_sms_received: 'Codes SMS reçus',
    stat_orders_completed: 'Commandes livrées',
    stat_active_creators: 'Créateurs & agences actifs',
    stat_uptime: 'Disponibilité du système',
    stat_speed: 'Délai moyen de démarrage',
    live_order_network: 'Réseau de commandes en direct',
    recent_activity: 'Activité récente',
    final_cta_title: 'Propulsez votre présence en ligne dès aujourd\'hui',
    final_cta_subtitle: 'Rejoignez plus de 50 000 créateurs, marques et agences qui se développent facilement avec NiceBooster.',
    create_free_account: 'Créer un compte gratuit',
    talk_to_support: 'Parler au support',
    footer_nav: 'Navigation',
    footer_account: 'Compte',
    footer_support: 'Support & Légal',

    // Common
    available_balance: 'Solde disponible',
    add_funds: 'Recharger',
    place_order: 'Commander',
    category: 'Catégorie',
    service: 'Service',
    quantity: 'Quantité',
    link: 'Lien',
    total_cost: 'Coût total',
    status: 'Statut',
    actions: 'Actions',
    search: 'Rechercher',
    cancel: 'Annuler',
    loading: 'Chargement...',
    close: 'Fermer',
  },
  es: {
    // Nav
    nav_dashboard: 'Panel de control',
    nav_new_order: 'Nuevo pedido',
    nav_services: 'Servicios',
    nav_orders: 'Pedidos',
    nav_deposit: 'Recargar',
    nav_virtual_numbers: 'Números virtuales',
    nav_referrals: 'Programa de referidos',
    nav_settings: 'Ajustes',
    nav_support: 'Soporte',
    nav_guide: 'Guía',
    nav_logout: 'Cerrar sesión',
    nav_login: 'Iniciar sesión',
    nav_signup: 'Empezar gratis',
    nav_features: 'Características',
    nav_platforms: 'Plataformas',
    nav_how_it_works: 'Cómo funciona',
    nav_pricing: 'Precios',
    nav_faq: 'Preguntas frecuentes',
    nav_language: 'Idioma',

    // Settings
    settings_title: 'Ajustes de la cuenta',
    settings_subtitle: 'Administre sus preferencias de perfil, moneda regional, seguridad y acceso a la API.',
    tab_preferences: 'Preferencias y moneda',
    tab_security: 'Seguridad y contraseña',
    tab_api: 'Clave API y revendedor',
    loc_title: 'Localización, moneda e idioma',
    loc_subtitle: 'Configure su país, idioma y moneda preferidos para sus pedidos y saldo.',
    field_country: 'País principal',
    field_country_desc: 'Las opciones de pago locales y Mobile Money se adaptarán a este país.',
    field_currency: 'Moneda de la cuenta',
    field_language: 'Idioma preferido',
    field_language_desc: 'Seleccione el idioma de visualización de la plataforma y notificaciones.',
    btn_save_preferences: 'Guardar preferencias',
    btn_saving_preferences: 'Guardando...',
    msg_preferences_saved: '¡Preferencias actualizadas con éxito!',

    // Dashboard
    dash_smm_services: 'Servicios de Redes Sociales',
    dash_smm_desc: 'Aumente sus seguidores, me gusta, vistas y compromiso en todas las plataformas',
    dash_vn_title: 'Números Virtuales',
    dash_vn_desc: 'Reciba códigos SMS de verificación al instante con números privados',
    dash_popular_smm: 'Servicios de Redes Populares',
    dash_see_all_services: 'Ver todos los servicios',
    dash_popular_vn: 'Números Virtuales Populares',
    dash_browse_countries: 'Ver todos los países',
    dash_top_up: '+ Recargar',
    dash_total_orders: 'Pedidos Totales',
    dash_all_time_completed: 'Total completados',
    dash_total_spent: 'Total Gastado',
    dash_lifetime_volume: 'Volumen total',
    dash_vn_used: 'Números Usados',
    dash_sms_verifications: 'Verificaciones SMS',
    dash_recent_orders: 'Pedidos Recientes',
    dash_recent_subtitle: 'Siga el estado de sus pedidos y entregas más recientes',
    dash_refresh: 'Actualizar',
    dash_view_all_orders: 'Ver Todos los Pedidos →',
    col_order: 'Pedido',
    col_service: 'Servicio',
    col_quantity: 'Cantidad',
    col_cost: 'Coste',
    col_status: 'Estado',
    col_date: 'Fecha',
    no_orders_placed: 'Aún no hay pedidos',
    no_orders_desc: 'Seleccione un servicio en nuestro catálogo para impulsar sus redes hoy.',
    place_first_order: 'Hacer Primer Pedido',

    // Landing Page
    hero_title_1: 'Acelere su Crecimiento en Redes a',
    hero_title_2: 'Precios Mayoristas',
    hero_subtitle: 'La plataforma automatizada todo en uno para creadores, agencias y empresas. Aumente seguidores, likes, vistas y obtenga números virtuales para SMS.',
    hero_cta_free: 'Empezar Gratis',
    hero_view_catalog: 'Ver Catálogo de Servicios',
    hero_trusted: 'Confiado por más de 50.000 creadores • MoMo y Cripto aceptados',
    stat_delivered_today: 'Entregados Hoy',
    stat_sms_received: 'SMS Recibidos',
    stat_orders_completed: 'Pedidos Completados',
    stat_active_creators: 'Creadores y Agencias Activos',
    stat_uptime: 'Disponibilidad del Sistema',
    stat_speed: 'Velocidad Media de Inicio',
    live_order_network: 'Red de Pedidos en Vivo',
    recent_activity: 'Actividad Reciente',
    final_cta_title: 'Potencie su Presencia en Redes Hoy Mismo',
    final_cta_subtitle: 'Únase a más de 50.000 creadores y marcas que crecen fácilmente con NiceBooster.',
    create_free_account: 'Crear Cuenta Gratis',
    talk_to_support: 'Hablar con Soporte',
    footer_nav: 'Navegación',
    footer_account: 'Cuenta',
    footer_support: 'Soporte y Legal',

    // Common
    available_balance: 'Saldo disponible',
    add_funds: 'Añadir fondos',
    place_order: 'Realizar pedido',
    category: 'Categoría',
    service: 'Servicio',
    quantity: 'Cantidad',
    link: 'Enlace',
    total_cost: 'Coste total',
    status: 'Estado',
    actions: 'Acciones',
    search: 'Buscar',
    cancel: 'Cancelar',
    loading: 'Cargando...',
    close: 'Cerrar',
  },
  ar: {
    // Nav
    nav_dashboard: 'لوحة التحكم',
    nav_new_order: 'طلب جديد',
    nav_services: 'الخدمات',
    nav_orders: 'الطلبات',
    nav_deposit: 'شحن الرصيد',
    nav_virtual_numbers: 'أرقام وهمية',
    nav_referrals: 'نظام الإحالة',
    nav_settings: 'الإعدادات',
    nav_support: 'الدعم الفني',
    nav_guide: 'دليل الاستخدام',
    nav_logout: 'تسجيل الخروج',
    nav_login: 'تسجيل الدخول',
    nav_signup: 'ابدأ الآن',
    nav_features: 'المميزات',
    nav_platforms: 'المنصات',
    nav_how_it_works: 'كيف يعمل',
    nav_pricing: 'الأسعار',
    nav_faq: 'الأسئلة الشائعة',
    nav_language: 'اللغة',

    // Settings
    settings_title: 'إعدادات الحساب',
    settings_subtitle: 'إدارة تفضيلات الحساب، العملة، الأمان ومفاتيح API.',
    tab_preferences: 'التفضيلات والعملة',
    tab_security: 'الأمان وكلمة المرور',
    tab_api: 'مفتاح API والموزع',
    loc_title: 'الموقع والعملة واللغة',
    loc_subtitle: 'حدد بلدك ولغتك المفضلة وعملتك للطلبات وطرق الدفع.',
    field_country: 'الدولة الأساسية',
    field_country_desc: 'خيارات الدفع وMobile Money ستكون مخصصة لهذه الدولة.',
    field_currency: 'عملة الحساب',
    field_language: 'اللغة المفضلة',
    field_language_desc: 'اختر لغة واجهة المنصة والإشعارات.',
    btn_save_preferences: 'حفظ التفضيلات',
    btn_saving_preferences: 'جاري التحديث...',
    msg_preferences_saved: 'تم تحديث التفضيلات بنجاح!',

    // Dashboard
    dash_smm_services: 'خدمات التواصل الاجتماعي',
    dash_smm_desc: 'زيادة المتابعين، الإعجابات، المشاهدات والتفاعل على جميع المنصات',
    dash_vn_title: 'الأرقام الوهمية',
    dash_vn_desc: 'استقبل أكواد التحقق SMS فوراً برقم خاص',
    dash_popular_smm: 'الخدمات الأكثر طلباً',
    dash_see_all_services: 'عرض جميع الخدمات',
    dash_popular_vn: 'الأرقام الوهمية الشائعة',
    dash_browse_countries: 'تصفح كل الدول',
    dash_top_up: '+ شحن الرصيد',
    dash_total_orders: 'إجمالي الطلبات',
    dash_all_time_completed: 'مكتملة بالكامل',
    dash_total_spent: 'إجمالي الإنفاق',
    dash_lifetime_volume: 'إجمالي الحجم',
    dash_vn_used: 'أرقام مستخدمة',
    dash_sms_verifications: 'تأكيدات SMS',
    dash_recent_orders: 'أحدث الطلبات',
    dash_recent_subtitle: 'تتبع حالة طلباتك الأخيرة والتقدم والتسليم',
    dash_refresh: 'تحديث',
    dash_view_all_orders: 'عرض جميع الطلبات ←',
    col_order: 'الطلب',
    col_service: 'الخدمة',
    col_quantity: 'الكمية',
    col_cost: 'التكلفة',
    col_status: 'الحالة',
    col_date: 'التاريخ',
    no_orders_placed: 'لا توجد طلبات بعد',
    no_orders_desc: 'اختر خدمة من كتالوجنا لبدء نمو حساباتك الآن.',
    place_first_order: 'طلب أول خدمة',

    // Landing Page
    hero_title_1: 'سرّع نمو حساباتك بأسعار',
    hero_title_2: 'الجملة الحصرية',
    hero_subtitle: 'المنصة الآلية المتكاملة لصناع المحتوى والوكالات. زيادة المتابعين والتفاعل، والحصول على أرقام وهمية لتفعيل الحسابات فوراً.',
    hero_cta_free: 'ابدأ مجاناً',
    hero_view_catalog: 'تصفح قائمة الخدمات',
    hero_trusted: 'موثوق من أكثر من 50,000 عميل • دعم MoMo والعملات الرقمية',
    stat_delivered_today: 'تم تسليمها اليوم',
    stat_sms_received: 'رسائل SMS مستلمة',
    stat_orders_completed: 'طلبات مكتملة',
    stat_active_creators: 'صناع محتوى ووكالات نشطة',
    stat_uptime: 'جاهزية النظام الآلي',
    stat_speed: 'متوسط سرعة البدء',
    live_order_network: 'شبكة الطلبات المباشرة',
    recent_activity: 'النشاط المباشر',
    final_cta_title: 'عزز حضورك الرقمي الآن',
    final_cta_subtitle: 'انضم لأكثر من 50,000 صانع محتوى وعلامة تجارية ينمون بسهولة مع NiceBooster.',
    create_free_account: 'إنشاء حساب مجاني',
    talk_to_support: 'تحدث مع الدعم',
    footer_nav: 'التنقل',
    footer_account: 'الحساب',
    footer_support: 'الدعم والشروط',

    // Common
    available_balance: 'الرصيد المتاح',
    add_funds: 'إضافة رصيد',
    place_order: 'تنفيذ الطلب',
    category: 'القسم',
    service: 'الخدمة',
    quantity: 'الكمية',
    link: 'الرابط',
    total_cost: 'التكلفة الإجمالية',
    status: 'الحالة',
    actions: 'الإجراءات',
    search: 'بحث',
    cancel: 'إلغاء',
    loading: 'جاري التحميل...',
    close: 'إغلاق',
  },
  pt: {
    // Nav
    nav_dashboard: 'Painel',
    nav_new_order: 'Novo Pedido',
    nav_services: 'Serviços',
    nav_orders: 'Pedidos',
    nav_deposit: 'Depositar',
    nav_virtual_numbers: 'Números Virtuais',
    nav_referrals: 'Programa de Afiliados',
    nav_settings: 'Configurações',
    nav_support: 'Suporte',
    nav_guide: 'Guia',
    nav_logout: 'Sair',
    nav_login: 'Entrar',
    nav_signup: 'Começar Grátis',
    nav_features: 'Recursos',
    nav_platforms: 'Plataformas',
    nav_how_it_works: 'Como Funciona',
    nav_pricing: 'Preços',
    nav_faq: 'Perguntas Frequentes',
    nav_language: 'Idioma',

    // Settings
    settings_title: 'Configurações da Conta',
    settings_subtitle: 'Gerencie suas preferências, moeda regional, credenciais e API.',
    tab_preferences: 'Preferências e Moeda',
    tab_security: 'Segurança e Senha',
    tab_api: 'Chave API e Revenda',
    loc_title: 'Localização, Moeda e Idioma',
    loc_subtitle: 'Defina seu país, idioma preferido e moeda para pedidos e saldo.',
    field_country: 'País Principal',
    field_country_desc: 'Opções de pagamento locais e Mobile Money usarão este país por padrão.',
    field_currency: 'Moeda da Conta',
    field_language: 'Idioma Preferido',
    field_language_desc: 'Selecione o idioma de exibição da plataforma e notificações.',
    btn_save_preferences: 'Salvar Preferências',
    btn_saving_preferences: 'Atualizando...',
    msg_preferences_saved: 'Preferências atualizadas com sucesso!',

    // Dashboard
    dash_smm_services: 'Serviços de Redes Sociais',
    dash_smm_desc: 'Aumente seus seguidores, curtidas, visualizações e engajamento em todas as redes',
    dash_vn_title: 'Números Virtuais',
    dash_vn_desc: 'Receba códigos de verificação SMS instantaneamente com números privados',
    dash_popular_smm: 'Serviços Populares',
    dash_see_all_services: 'Ver todos os serviços',
    dash_popular_vn: 'Números Virtuais Populares',
    dash_browse_countries: 'Ver todos os países',
    dash_top_up: '+ Recarregar',
    dash_total_orders: 'Total de Pedidos',
    dash_all_time_completed: 'Concluídos com sucesso',
    dash_total_spent: 'Total Gasto',
    dash_lifetime_volume: 'Volume acumulado',
    dash_vn_used: 'Números Usados',
    dash_sms_verifications: 'Verificações SMS',
    dash_recent_orders: 'Pedidos Recentes',
    dash_recent_subtitle: 'Acompanhe seus pedidos mais recentes e status de entrega',
    dash_refresh: 'Atualizar',
    dash_view_all_orders: 'Ver Todos os Pedidos →',
    col_order: 'Pedido',
    col_service: 'Serviço',
    col_quantity: 'Quantidade',
    col_cost: 'Custo',
    col_status: 'Status',
    col_date: 'Data',
    no_orders_placed: 'Nenhum pedido realizado ainda',
    no_orders_desc: 'Escolha um serviço em nosso catálogo para acelerar seus perfis hoje.',
    place_first_order: 'Fazer Primeiro Pedido',

    // Landing Page
    hero_title_1: 'Acelere seu Crescimento Social a',
    hero_title_2: 'Preços de Atacado',
    hero_subtitle: 'A plataforma automatizada tudo-em-um para criadores, agências e empresas. Aumente seguidores, curtidas, visualizações e obtenha números virtuais para SMS.',
    hero_cta_free: 'Começar Grátis',
    hero_view_catalog: 'Ver Catálogo de Serviços',
    hero_trusted: 'Aprovado por mais de 50.000 criadores • MoMo e Cripto aceitos',
    stat_delivered_today: 'Entregues Hoje',
    stat_sms_received: 'SMS Recebidos',
    stat_orders_completed: 'Pedidos Concluídos',
    stat_active_creators: 'Criadores & Agências Ativas',
    stat_uptime: 'Disponibilidade do Sistema',
    stat_speed: 'Início Médio dos Pedidos',
    live_order_network: 'Rede de Pedidos em Tempo Real',
    recent_activity: 'Atividade Recente',
    final_cta_title: 'Turbine sua Presença nas Redes Hoje Mesmo',
    final_cta_subtitle: 'Junte-se a mais de 50.000 criadores e marcas que crescem facilmente com o NiceBooster.',
    create_free_account: 'Criar Conta Gratuita',
    talk_to_support: 'Falar com o Suporte',
    footer_nav: 'Navegação',
    footer_account: 'Conta',
    footer_support: 'Suporte e Termos',

    // Common
    available_balance: 'Saldo Disponível',
    add_funds: 'Adicionar Saldo',
    place_order: 'Fazer Pedido',
    category: 'Categoria',
    service: 'Serviço',
    quantity: 'Quantidade',
    link: 'Link',
    total_cost: 'Custo Total',
    status: 'Status',
    actions: 'Ações',
    search: 'Pesquisar',
    cancel: 'Cancelar',
    loading: 'Carregando...',
    close: 'Fechar',
  }
};

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    const saved = localStorage.getItem('nb_language');
    if (saved && TRANSLATIONS[saved]) return saved;
    // Auto-detect browser language if french
    const browserLang = (navigator.language || navigator.userLanguage || '').substring(0, 2).toLowerCase();
    if (TRANSLATIONS[browserLang]) return browserLang;
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('nb_language', language);
    document.documentElement.setAttribute('lang', language);
    const langObj = SUPPORTED_LANGUAGES.find(l => l.code === language);
    if (langObj?.dir === 'rtl') {
      document.documentElement.setAttribute('dir', 'rtl');
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
    }
  }, [language]);

  const setLanguage = (newLang) => {
    if (TRANSLATIONS[newLang]) {
      setLanguageState(newLang);
    }
  };

  const t = useMemo(() => {
    return (key, fallback) => {
      const currentDict = TRANSLATIONS[language] || TRANSLATIONS.en;
      if (currentDict && currentDict[key]) return currentDict[key];
      if (TRANSLATIONS.en[key]) return TRANSLATIONS.en[key];
      return fallback !== undefined ? fallback : key;
    };
  }, [language]);

  const currentLanguageObj = useMemo(() => {
    return SUPPORTED_LANGUAGES.find(l => l.code === language) || SUPPORTED_LANGUAGES[0];
  }, [language]);

  return (
    <LanguageContext.Provider value={{
      language,
      setLanguage,
      t,
      languages: SUPPORTED_LANGUAGES,
      currentLanguage: currentLanguageObj
    }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
